/**
 * Temporary list filters (the prototype's `filtering()`): unread only, source, topic, sort, and — for
 * search — "All folders". Separate from saved Views and from automation Filters; kept in the UI store.
 */

import { useState, type FormEvent } from 'react';
import type { SourceId, TopicId } from '@digester/core';
import { DialogFrame, type DialogProps } from '../app/dialogs.js';
import { DEFAULT_LIST_FILTER, ui, type ListFilter } from '../app/ui-store.js';
import { Select, Toggle } from '../ui/form.js';
import { useLookups } from './data.js';

export function ListFilterDialog({ close, dialogId }: DialogProps) {
  const lookups = useLookups();
  const [draft, setDraft] = useState<ListFilter>(() => ui.get().listFilter);
  const set = (patch: Partial<ListFilter>): void => setDraft((d) => ({ ...d, ...patch }));
  const apply = (e: FormEvent): void => {
    e.preventDefault();
    ui.set({ listFilter: draft, mobileOverlay: 'none' });
    close();
  };
  const sources = lookups.sources.filter((s) => s.archivedAt === null);
  const topics = lookups.topics.filter((t) => t.archivedAt === null);
  return (
    <DialogFrame title="Filter messages" className="small-dialog" onClose={close} dialogId={dialogId}>
      <form id="filter-form" onSubmit={apply}>
        <Toggle label="Unread only" name="unread" checked={draft.unreadOnly} onChange={(unreadOnly) => set({ unreadOnly })} />
        <Select
          label="Source"
          name="source"
          value={draft.sourceId ?? 'all'}
          options={[['all', 'All sources'] as const, ...sources.map((s) => [s.id, s.name] as const)]}
          onChange={(v) => set({ sourceId: v === 'all' ? null : (v as SourceId) })}
        />
        <Select
          label="Topic"
          name="topic"
          value={draft.topicId ?? 'all'}
          options={[['all', 'All topics'] as const, ...topics.map((t) => [t.id, t.name] as const)]}
          onChange={(v) => set({ topicId: v === 'all' ? null : (v as TopicId) })}
        />
        <Select
          label="Sort"
          name="sort"
          value={draft.sort}
          options={[
            ['newest', 'Newest first'],
            ['relevance', 'Highest relevance'],
          ]}
          onChange={(sort) => set({ sort })}
        />
        <Toggle label="Search all folders" help="Search every folder instead of the open one" name="allFolders" checked={draft.allFolders} onChange={(allFolders) => set({ allFolders })} />
        <div className="dialog-footer">
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              ui.set({ listFilter: DEFAULT_LIST_FILTER, search: '' });
              close();
            }}
          >
            Clear
          </button>
          <button className="primary-button" type="submit">
            Apply
          </button>
        </div>
      </form>
    </DialogFrame>
  );
}
